<script lang="ts" setup>
import { useDevice } from '#imports'
import { useI18n } from 'vue-i18n'
import { ref, computed } from 'vue'
import getCountryCode from '../../../utils/shared/countryCode'

import ChargemapLogo from '../../images/ChargemapLogo.vue'
import ChargemapB2CButton from './ChargemapB2CButton.vue'
import MenuButton from './navigation/MenuButton.vue'

const { isIos } = useDevice()
const { t } = useI18n()

const props = defineProps<{
    locale?: string
    s3Url: string
    userId: number
    defaultAvatarPath: string
    userAvatar?: string
    route: string
    cdnUrl?: string
}>()

const isMenuOpen = ref(false) // State to track menu open/close

const chargemapLogoSquarePath = ref(
    props.cdnUrl ? `${props.cdnUrl}/images/logo-square-light.svg` : ''
)

const menuButtons = computed(() => {
    return [
        {
            class: 'gtm-NavBarPrincipale',
            label: t('word.map'),
            href: '/map',
            active: isCurrentRoute('map')
        },
        {
            class: 'gtm-NavBarPrincipale',
            label: t('word.mobile'),
            href: '/mobile',
            active: isCurrentRoute('mobile')
        },
        {
            class: 'gtm-NavBarPrincipale md:hidden',
            label: t('word.planner'),
            href: '/mobile/planner',
            active: isCurrentRoute('mobile/planner')
        },
        {
            class: 'gtm-NavBarPrincipale',
            label: t('chargemap_pass'),
            href: '/pass',
            active: isCurrentRoute('pass')
        },
        {
            class: 'gtm-NavBarPrincipale',
            label: t('word.community'),
            href: '/community',
            active: isCurrentRoute('community')
        },
        {
            class: 'gtm-NavBarPrincipale',
            label: t('word.companies'),
            href: businessUrl.value
        },
        {
            class: 'gtm-NavBarPrincipale md:hidden',
            label: t('action.download_app'),
            href: storeUrl.value,
            active: true
        }
    ]
})

const userAvatarUrl = computed(() => {
    if (!props.userId || !props.userAvatar) {
        return props.defaultAvatarPath
    }

    return `${props.s3Url}/users/avatar/200x200/${props.userId}/${props.userAvatar}`
})

const businessUrl = computed(() => {
    let language = ''
    switch (props.locale) {
        case 'de':
            language = '/de'
            break
        case 'en':
        case undefined:
        case '':
            language = '/en'
            break
        default:
            language = ''
    }

    return `https://chargemap-business.com${language}`
})

const storeUrl = computed(() =>
    isIos
        ? `https://itunes.apple.com/${getCountryCode(props.locale || 'en')}/app/chargemap-bornes-de-recharge/id438176982`
        : `https://play.google.com/store/apps/details?id=com.chargemap_beta.android&hl=${props.locale || 'en'}`
)

const isCurrentRoute = (routeName: string) => props.route === routeName
</script>

<template>
    <nav>
        <div
            class="flex items-center w-full md:w-auto h-14"
            :class="[userId && 'justify-between']"
        >
            <button
                id="burger"
                aria-label="Menu"
                class="normal md:hidden"
                type="button"
                :class="isMenuOpen ? 'active' : ''"
                @click="isMenuOpen = !isMenuOpen"
            >
                <span />
                <span />
                <span />
                <span />
            </button>
            <a
                class="absolute w-[190px] left-1/2 transform -translate-x-2/4 flex items-center justify-start md:relative"
                title="Chargemap"
                href="/"
            >
                <ChargemapLogo
                    class="gtm-NavBarPrincipale h-10 w-40 hidden min-[425px]:block"
                />
                <NuxtImg
                    :src="chargemapLogoSquarePath"
                    alt="Chargemap"
                    class="gtm-NavBarPrincipale h-10 w-40 min-[425px]:hidden"
                />
            </a>
            <a
                v-if="userId"
                class="md:!hidden avatar"
                href="/user/index"
                :title="t('menu.your_account')"
            >
                <NuxtImg :src="userAvatarUrl" :alt="t('menu.your_account')" />
            </a>
        </div>

        <!--      MOBILE  -->
        <div
            v-if="isMenuOpen"
            class="flex md:hidden flex-col justify-start w-full bg-white transition-nav-bar duration-500 ease-in-out overflow-y-hidden z-10 pb-2 border-b border-gray-navbar max-h-96"
        >
            <div class="menu-buttons-container">
                <MenuButton
                    v-for="button in menuButtons"
                    :key="button.label"
                    v-bind="button"
                />
            </div>
            <div v-if="!userId" class="buttons-container">
                <a
                    href="/signin"
                    :title="t('menu.signin')"
                    class="gtm-NavBarPrincipale"
                >
                    <ChargemapB2CButton type="primary" size="medium">
                        <IconLogin class="size-4" />
                        {{ t('menu.signin') }}
                    </ChargemapB2CButton>
                </a>
                <a
                    href="/register"
                    :title="t('menu.sign_up')"
                    class="gtm-NavBarPrincipale"
                >
                    <ChargemapB2CButton type="secondary" size="medium">
                        {{ t('menu.sign_up') }}
                    </ChargemapB2CButton>
                </a>
            </div>
        </div>

        <!--     DESKTOP  -->
        <div
            class="hidden md:flex flex-row items-center gap-2 w-auto relative bg-white border-0 max-h-full transition-nav-bar duration-500 ease-in-out overflow-hidden transition-none"
        >
            <div class="menu-buttons-container">
                <MenuButton
                    v-for="button in menuButtons"
                    :key="button.label"
                    v-bind="button"
                />
            </div>
            <a
                v-if="userId"
                class="avatar"
                href="/user/index"
                :title="t('menu.your_account')"
            >
                <NuxtImg :src="userAvatarUrl" :alt="t('menu.your_account')" />
                <MenuButton
                    :label="t('menu.your_account')"
                    href="/user/index"
                    class="hidden md:block"
                />
            </a>
            <div v-else class="buttons-container">
                <a
                    href="/signin"
                    :title="t('menu.signin')"
                    class="gtm-NavBarPrincipale"
                >
                    <ChargemapB2CButton type="primary" size="medium">
                        <IconLogin class="size-4" />
                        {{ t('menu.signin') }}
                    </ChargemapB2CButton>
                </a>
                <a
                    href="/register"
                    :title="t('menu.sign_up')"
                    class="gtm-NavBarPrincipale"
                >
                    <ChargemapB2CButton type="secondary" size="medium">
                        {{ t('menu.sign_up') }}
                    </ChargemapB2CButton>
                </a>
            </div>
        </div>
    </nav>
</template>

<style scoped>
nav {
  @apply w-full min-h-14 px-15 bg-white flex flex-col md:flex-row items-center md:justify-between z-10;
}
nav .menu-buttons-container {
  @apply flex flex-col md:flex-row items-start md:items-center justify-center gap-5 md:gap-3.5;
}
nav #burger {
  @apply relative transform rotate-0 ease-in-out duration-500 h-3.5 w-[22px];
}
nav #burger.active span:nth-child(1), nav #burger.active span:nth-child(4) {
  @apply left-1/2 w-0 top-1.5;
}
nav #burger.active span:nth-child(2) {
  @apply transform rotate-45;
}
nav #burger.active span:nth-child(3) {
  @apply transform -rotate-45;
}
nav #burger:hover > span {
  @apply bg-sky-500;
}
nav #burger span {
  @apply block absolute bg-zinc-800 opacity-100 left-0 transform rotate-0 ease-in-out duration-200 w-full h-0.5;
}
nav #burger span:nth-child(1) {
  @apply top-0;
}
nav #burger span:nth-child(2), nav #burger span:nth-child(3) {
  @apply top-1.5;
}
nav #burger span:nth-child(4) {
  @apply top-3;
}
nav .avatar {
  @apply flex justify-start items-center gap-2;
}
nav .avatar:focus {
  @apply no-underline outline-none;
}
nav .avatar img {
  @apply size-10 md:size-8 rounded;
}
nav .buttons-container {
  @apply flex gap-2 mt-3 md:mt-0;
}
</style>
